import {
  databases,
  DATABASE_ID,
  COLLECTION_ID_POLES_DELIVERIES
} from '../config/appwrite';
import { ID, Query } from 'appwrite';

/**
 * Poles deliveries persistence layer.
 *
 * One Appwrite document per Poles spreadsheet row. The `rowKey` attribute is
 * a stable digest built from the row's Hamlet identity columns (see
 * `getPolesRowKey` in `DeliveryTracking.js`); `edits` holds a JSON object of
 * every editable cell value for that row (Delivered Quantities, DN/Date/Supplier,
 * PO/Invoice, Comment, etc.). Survey Quantity fields are stripped and no longer
 * persisted.
 *
 * Two operations are exposed:
 *   - `fetchPolesEditsMap()` reads the full state into a `{ [rowKey]: edits }`
 *     map so the UI can merge it on top of the Excel-derived baseline.
 *   - `upsertPolesEdit(rowKey, edits)` writes a single row's edits, with the
 *     `$id` of the existing document discovered via a `rowKey` equality query.
 */

/** Canonical survey / BOQ header matchers (must stay aligned with DeliveryTracking). */
const POLES_BOQ_MATCHERS = [
  (h) => h.includes('9m') && h.includes('medium'),
  (h) => h.includes('10m') && h.includes('medium'),
  (h) => h.includes('10m') && h.includes('stout'),
  (h) => h.includes('13m') && h.includes('medium'),
  (h) => h.includes('13m') && h.includes('stout'),
  (h) => h.includes('12m') && h.includes('medium'),
  (h) => h.includes('12m') && h.includes('stout'),
  (h) => h.includes('10m') && h.includes('concrete'),
  (h) => h.includes('13m') && h.includes('concrete')
];

const isPolesBoqHeader = (header) => {
  const hl = String(header || '').toLowerCase();
  return POLES_BOQ_MATCHERS.some((match) => match(hl));
};

/** True for Survey Quantity edit keys (BOQ labels without the "(D)" suffix). */
export const isSurveyQuantityEditKey = (key) => {
  const k = String(key || '').trim();
  if (!k || /\(\s*d\s*\)\s*$/i.test(k)) return false;
  return isPolesBoqHeader(k);
};

/** Remove Survey Quantity fields from a poles edits object. */
export const stripSurveyQuantityEdits = (edits) => {
  if (!edits || typeof edits !== 'object') return {};
  const next = {};
  Object.keys(edits).forEach((key) => {
    if (!isSurveyQuantityEditKey(key)) next[key] = edits[key];
  });
  return next;
};

const parseEditsJson = (raw) => {
  try {
    const parsed = typeof raw === 'string' ? JSON.parse(raw || '{}') : raw || {};
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
};

/**
 * Read every persisted row's edits and return them keyed by `rowKey`. Pages
 * through Appwrite's listDocuments with a cursor so we don't truncate at 100
 * documents once the dataset grows.
 */
export const fetchPolesEditsMap = async () => {
  const map = {};
  let cursor = null;
  const limit = 100;
  for (let page = 0; page < 50; page += 1) {
    const queries = [Query.limit(limit), Query.orderDesc('$createdAt')];
    if (cursor) queries.push(Query.cursorAfter(cursor));
    const response = await databases.listDocuments(
      DATABASE_ID,
      COLLECTION_ID_POLES_DELIVERIES,
      queries
    );
    for (const doc of response.documents) {
      const rowKey = doc.rowKey;
      if (!rowKey) continue;
      map[rowKey] = stripSurveyQuantityEdits(parseEditsJson(doc.edits));
    }
    if (response.documents.length < limit) break;
    cursor = response.documents[response.documents.length - 1].$id;
  }
  return map;
};

/**
 * Upsert one row's edits. Pass the full edits object for that row, not a
 * patch — the caller (`DeliveryTracking.js`) merges per-cell changes into its
 * in-memory state first and then sends the consolidated view here, which
 * keeps the schema simple (single attribute, no partial update conflicts).
 * Survey Quantity keys are stripped before write.
 */
export const upsertPolesEdit = async (rowKey, edits) => {
  const cleanKey = String(rowKey || '').trim();
  if (!cleanKey) return null;
  const cleanedEdits = stripSurveyQuantityEdits(edits);
  const payload = {
    rowKey: cleanKey,
    edits: JSON.stringify(cleanedEdits)
  };

  const existing = await databases.listDocuments(
    DATABASE_ID,
    COLLECTION_ID_POLES_DELIVERIES,
    [Query.equal('rowKey', cleanKey), Query.limit(1)]
  );

  if (existing.documents.length > 0) {
    return databases.updateDocument(
      DATABASE_ID,
      COLLECTION_ID_POLES_DELIVERIES,
      existing.documents[0].$id,
      payload
    );
  }
  return databases.createDocument(
    DATABASE_ID,
    COLLECTION_ID_POLES_DELIVERIES,
    ID.unique(),
    payload
  );
};
